import { useEffect, useState } from "react";
import {
  DEFAULT_JOINT_VISIBILITY_UI_STATE,
  JOINT_MARKER_SCALE_DEFAULT_PERCENT,
  JOINT_MARKER_SCALE_EVENT,
  JOINT_POSITION_SLIDER_VISIBILITY_EVENT,
  JOINT_VISIBILITY_UI_EVENT,
  clampJointMarkerScalePercent,
  readJointMarkerScalePercent,
} from "../../selected-joint-visibility";
import {
  DEFAULT_SITE_VISIBILITY_UI_STATE,
  SITE_VISIBILITY_UI_EVENT,
} from "../../selected-site-visibility";
import {
  BODY_VISIBILITY_UI_EVENT,
  DEFAULT_BODY_VISIBILITY_UI_STATE,
} from "../../selected-body-visibility";
import {
  DEFAULT_INERTIAL_VISIBILITY_UI_STATE,
  INERTIAL_VISIBILITY_UI_EVENT,
} from "../../selected-inertial-visibility";
import {
  CONSTRAINT_VISIBILITY_UI_EVENT,
  DEFAULT_CONSTRAINT_VISIBILITY_UI_STATE,
} from "../../selected-constraint-visibility/visibilityEvents";
import {
  DEFAULT_SCENE_INVENTORY_LABEL_VISIBILITY_STATE,
  SCENE_INVENTORY_LABEL_VISIBILITY_EVENT,
} from "../../scene-inventory-labels";
import {
  DEFAULT_SELECTION_HIGHLIGHT_UI_STATE,
  SELECTION_HIGHLIGHT_UI_EVENT,
} from "../../selected-highlight-visibility";
import { computeJointMarkerSizesFromModelGroup } from "../scene/jointMarkerSizing";

export function useSceneVisibilityUiSubscriptions({
  sceneRef,
  applyJointMarkerSizes,
  setStructureChangeTick,
}) {
  const [jointMarkerScalePercent, setJointMarkerScalePercent] = useState(() =>
    readJointMarkerScalePercent()
  );
  const [jointVisibilityUi, setJointVisibilityUi] = useState(DEFAULT_JOINT_VISIBILITY_UI_STATE);
  const [showJointPositionSlider, setShowJointPositionSlider] = useState(false);
  const [showViewportBodies, setShowViewportBodies] = useState(true);
  const [bodyVisibilityUi, setBodyVisibilityUi] = useState(DEFAULT_BODY_VISIBILITY_UI_STATE);
  const [showSavedCameraMarkers, setShowSavedCameraMarkers] = useState(false);
  const [siteVisibilityUi, setSiteVisibilityUi] = useState(DEFAULT_SITE_VISIBILITY_UI_STATE);
  const [inertialVisibilityUi, setInertialVisibilityUi] = useState(
    DEFAULT_INERTIAL_VISIBILITY_UI_STATE
  );
  const [constraintVisibilityUi, setConstraintVisibilityUi] = useState(
    DEFAULT_CONSTRAINT_VISIBILITY_UI_STATE
  );
  const [inventoryLabelVisibility, setInventoryLabelVisibility] = useState(
    DEFAULT_SCENE_INVENTORY_LABEL_VISIBILITY_STATE
  );
  const [selectionHighlightUi, setSelectionHighlightUi] = useState(
    DEFAULT_SELECTION_HIGHLIGHT_UI_STATE
  );
  const [showRotationGizmo, setShowRotationGizmo] = useState(false);
  const [showScaleGizmo, setShowScaleGizmo] = useState(false);

  useEffect(() => {
    const onStructure = () => setStructureChangeTick((v) => v + 1);
    window.addEventListener("structure-changed", onStructure);
    return () => window.removeEventListener("structure-changed", onStructure);
  }, [setStructureChangeTick]);

  useEffect(() => {
    const handleJointVisibilityUiChange = (event) => {
      const detail = event?.detail || {};
      setJointVisibilityUi({
        focus: !!detail.focus,
        labelsOff: !!detail.labelsOff,
        frameAxes: !!detail.frameAxes,
        positionSlider: !!detail.positionSlider,
      });
    };
    window.addEventListener(JOINT_VISIBILITY_UI_EVENT, handleJointVisibilityUiChange);
    return () => {
      window.removeEventListener(JOINT_VISIBILITY_UI_EVENT, handleJointVisibilityUiChange);
    };
  }, []);

  useEffect(() => {
    const handleJointPositionSliderVisibility = (event) => {
      setShowJointPositionSlider(!!event?.detail?.visible);
    };
    window.addEventListener(
      JOINT_POSITION_SLIDER_VISIBILITY_EVENT,
      handleJointPositionSliderVisibility
    );
    return () => {
      window.removeEventListener(
        JOINT_POSITION_SLIDER_VISIBILITY_EVENT,
        handleJointPositionSliderVisibility
      );
    };
  }, []);

  useEffect(() => {
    const handleJointMarkerScaleChanged = (event) => {
      const nextPercent = clampJointMarkerScalePercent(
        Number(event?.detail?.percent ?? JOINT_MARKER_SCALE_DEFAULT_PERCENT)
      );
      setJointMarkerScalePercent(nextPercent);
      const modelGroup = sceneRef.current?.getObjectByName("modelGroup");
      const sizes = computeJointMarkerSizesFromModelGroup(modelGroup, nextPercent);
      applyJointMarkerSizes(sizes);
      setStructureChangeTick((v) => v + 1);
    };
    window.addEventListener(JOINT_MARKER_SCALE_EVENT, handleJointMarkerScaleChanged);
    return () => {
      window.removeEventListener(JOINT_MARKER_SCALE_EVENT, handleJointMarkerScaleChanged);
    };
  }, [applyJointMarkerSizes, sceneRef, setStructureChangeTick]);

  useEffect(() => {
    const handleSiteVisibilityUiChange = (event) => {
      const detail = event?.detail || {};
      setSiteVisibilityUi({
        focus: !!detail.focus,
        labelsOff: !!detail.labelsOff,
        orientationAxes: !!detail.orientationAxes,
      });
    };
    window.addEventListener(SITE_VISIBILITY_UI_EVENT, handleSiteVisibilityUiChange);
    return () => {
      window.removeEventListener(SITE_VISIBILITY_UI_EVENT, handleSiteVisibilityUiChange);
    };
  }, []);

  useEffect(() => {
    const handleInertialVisibilityUiChange = (event) => {
      const detail = event?.detail || {};
      setInertialVisibilityUi({
        focus: !!detail.focus,
        orientationAxes: !!detail.orientationAxes,
      });
    };
    window.addEventListener(INERTIAL_VISIBILITY_UI_EVENT, handleInertialVisibilityUiChange);
    return () => {
      window.removeEventListener(INERTIAL_VISIBILITY_UI_EVENT, handleInertialVisibilityUiChange);
    };
  }, []);

  useEffect(() => {
    const handleConstraintVisibilityUiChange = (event) => {
      const detail = event?.detail || {};
      setConstraintVisibilityUi({
        focus: !!detail.focus,
        constraintsVisible: detail.constraintsVisible !== false,
        tendonsVisible: detail.tendonsVisible !== false,
        labelsOff: !!detail.labelsOff,
        tendonLabelsOff: !!detail.tendonLabelsOff,
      });
    };
    window.addEventListener(CONSTRAINT_VISIBILITY_UI_EVENT, handleConstraintVisibilityUiChange);
    return () => {
      window.removeEventListener(
        CONSTRAINT_VISIBILITY_UI_EVENT,
        handleConstraintVisibilityUiChange
      );
    };
  }, []);

  useEffect(() => {
    const handleSelectionHighlightUiChange = (event) => {
      const detail = event?.detail || {};
      const targetType = detail.targetType;
      if (!Object.prototype.hasOwnProperty.call(DEFAULT_SELECTION_HIGHLIGHT_UI_STATE, targetType)) {
        return;
      }
      setSelectionHighlightUi((prev) => ({
        ...prev,
        [targetType]: detail.enabled !== false,
      }));
    };
    window.addEventListener(SELECTION_HIGHLIGHT_UI_EVENT, handleSelectionHighlightUiChange);
    return () => {
      window.removeEventListener(SELECTION_HIGHLIGHT_UI_EVENT, handleSelectionHighlightUiChange);
    };
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const visible = (event?.detail?.visible ?? true) === true;
      setShowViewportBodies(visible);
    };
    window.addEventListener("viewport-body-visibility", handler);
    return () => window.removeEventListener("viewport-body-visibility", handler);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const detail = event?.detail || {};
      setBodyVisibilityUi({
        labels: !!detail.labels,
        objectLabels: !!detail.objectLabels,
        bodyLabelMarkers: !!detail.bodyLabelMarkers,
      });
    };
    window.addEventListener(BODY_VISIBILITY_UI_EVENT, handler);
    return () => window.removeEventListener(BODY_VISIBILITY_UI_EVENT, handler);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const detail = event?.detail || {};
      setInventoryLabelVisibility({
        ...DEFAULT_SCENE_INVENTORY_LABEL_VISIBILITY_STATE,
        ...(detail || {}),
      });
    };
    window.addEventListener(SCENE_INVENTORY_LABEL_VISIBILITY_EVENT, handler);
    return () => window.removeEventListener(SCENE_INVENTORY_LABEL_VISIBILITY_EVENT, handler);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const visible = (event?.detail?.visible ?? false) === true;
      setShowSavedCameraMarkers(visible);
    };
    window.addEventListener("viewport-camera-visibility", handler);
    return () => window.removeEventListener("viewport-camera-visibility", handler);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const visible = (event?.detail?.visible ?? false) === true;
      setShowRotationGizmo(visible);
    };
    window.addEventListener("rotation-gizmo-visibility", handler);
    return () => window.removeEventListener("rotation-gizmo-visibility", handler);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const visible = (event?.detail?.visible ?? false) === true;
      setShowScaleGizmo(visible);
    };
    window.addEventListener("scale-gizmo-visibility", handler);
    return () => window.removeEventListener("scale-gizmo-visibility", handler);
  }, []);

  return {
    bodyVisibilityUi,
    constraintVisibilityUi,
    inertialVisibilityUi,
    inventoryLabelVisibility,
    jointMarkerScalePercent,
    jointVisibilityUi,
    selectionHighlightUi,
    showJointPositionSlider,
    showRotationGizmo,
    showSavedCameraMarkers,
    showScaleGizmo,
    showViewportBodies,
    siteVisibilityUi,
  };
}
